import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, Clock, ArrowRight, Sparkles } from "lucide-react";
import { allBlogArticles } from "@/data/blogArticles";
import { BLOG_IMAGES } from "@/data/blogImages";

const imageForArticle = new Map(allBlogArticles.map((a, i) => [a.id, BLOG_IMAGES[i % BLOG_IMAGES.length]]));

const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", "AI & E-commerce", "Quick Commerce", "E-commerce", "Digital Commerce", "Platform Guides", "Premium Platforms", "International", "Private Labeling", "Strategy", "Growth Tips"];

  const filteredArticles = allBlogArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (article.keywords && article.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase())));
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>E-commerce & Marketplace Blog | BrandBorn - Onboarding Guides, Tips & Strategies</title>
        <meta name="description" content="Read 100+ expert articles on marketplace onboarding, quick commerce strategy, Myntra seller guide, Nykaa registration, Blinkit vendor tips. Free guides by BrandBorn." />
        <link rel="canonical" href="https://www.brandborn.in/blog" />
        <meta property="og:title" content="E-commerce Blog | BrandBorn - Marketplace Onboarding Guides" />
        <meta property="og:description" content="100+ expert articles on selling on Myntra, Nykaa, Ajio, Blinkit, Zepto. Free marketplace onboarding guides." />
        <meta property="og:url" content="https://www.brandborn.in/blog" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-red-50 via-white to-orange-50 relative overflow-hidden">
        <div className="absolute top-20 right-[10%] w-[400px] h-[400px] bg-gradient-to-br from-red-200/30 to-orange-200/30 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full text-sm font-bold mb-6 shadow-lg">
              <Sparkles size={16} />
              KNOWLEDGE HUB
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6">
              E-commerce <span className="text-gradient bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Blog & Guides</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert insights on e-commerce onboarding, quick commerce, digital marketplace strategies, and growth tips for your brand.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search articles by keyword, topic, or platform..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-red-500 focus:ring-0 text-gray-900 bg-white shadow-lg"
              />
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-red-300"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <p className="text-gray-600">
              Showing <span className="font-bold text-gray-900">{filteredArticles.length}</span> articles
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, idx) => (
              <Link
                key={article.id || idx}
                to={`/blog/${article.id}`}
                className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-red-200 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-50 overflow-hidden relative">
                  <img
                    src={imageForArticle.get(article.id) || article.image || BLOG_IMAGES[idx % BLOG_IMAGES.length]}
                    alt={article.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-red-50 text-red-600 text-xs font-bold rounded-full">
                      {article.category}
                    </span>
                    <span className="flex items-center gap-1 text-gray-500 text-xs">
                      <Clock size={12} />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-red-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center text-red-500 font-semibold text-sm group-hover:gap-2 transition-all">
                    Read More <ArrowRight size={16} className="ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-red-500 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Ready to Launch Your Brand?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get expert help with your e-commerce onboarding journey
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white text-red-500 px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all"
          >
            Contact Us <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <style>{`
        .text-gradient {
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BlogPage;
